import React from 'react';

const CookiePolicy = () => {
  const revokeCookieConsent = () => {
    // Check if window.cookieconsent is available
    if (window.cookieconsent && window.cookieconsent.utils) {
      // This will reset the user's cookie choice, causing the popup to reappear
      window.cookieconsent.utils.setCookie('cookieconsent_status', '', { expires: -1 });
      // Reload the page to make the banner appear again
      window.location.reload();
    } else {
      console.error('Cookie consent library not available');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-black shadow-md rounded-lg mt-24 mb-16">
      <h1 className="text-3xl font-bold mb-4 text-white">Cookie Policy</h1>
      <p className="text-sm text-gray-400 mb-6">
        <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
      </p>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold mb-3 text-white">What Are Cookies</h2>
        <p className="text-white leading-relaxed">
          Cookies are small text files that are placed on your computer, mobile device, or tablet by websites that you visit. 
          They are widely used to make websites work more efficiently and to provide information to the owners of the site.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold mb-3 text-white">How We Use Cookies</h2>
        <p className="text-white leading-relaxed mb-3">
          HumanAI uses cookies for several purposes, including:
        </p>
        <ul className="list-disc list-inside text-white leading-relaxed space-y-2 ml-4">
          <li><strong>Essential cookies:</strong> Required for the operation of our website. They enable basic functions like page navigation and access to secure areas of the website.</li>
          <li><strong>Analytical/performance cookies:</strong> Allow us to recognize and count the number of visitors and see how visitors move around our website when they're using it.</li>
          <li><strong>Functionality cookies:</strong> Used to recognize you when you return to our website. This enables us to personalize our content for you and remember your preferences.</li>
          <li><strong>Targeting cookies:</strong> Record your visit to our website, the pages you have visited, and the links you have followed. We may use this information to make our website and advertising more relevant to your interests.</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold mb-3 text-white">Third-Party Cookies</h2>
        <p className="text-white leading-relaxed mb-3">
          In addition to our own cookies, we may also use various third-party cookies to report usage statistics of the website and deliver advertisements on and through the website.
          These third parties may include:
        </p>
        <ul className="list-disc list-inside text-white leading-relaxed space-y-2 ml-4">
          <li><strong>Google Analytics:</strong> To understand how visitors interact with our website</li>
          <li><strong>Social media platforms:</strong> For social sharing functionality</li>
          <li><strong>Email service providers:</strong> For newsletter signup and communication preferences</li>
          <li><strong>Customer support tools:</strong> To provide better user assistance</li>
        </ul>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold mb-3 text-white">Specific Cookies We Use</h2>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-600 text-sm">
            <thead>
              <tr className="bg-gray-800">
                <th className="border border-gray-600 px-4 py-2 text-left text-white">Cookie Name</th>
                <th className="border border-gray-600 px-4 py-2 text-left text-white">Purpose</th>
                <th className="border border-gray-600 px-4 py-2 text-left text-white">Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-600 px-4 py-2 text-white">cookieconsent_status</td>
                <td className="border border-gray-600 px-4 py-2 text-white">Stores your cookie consent preferences</td>
                <td className="border border-gray-600 px-4 py-2 text-white">1 year</td>
              </tr>
              <tr className="bg-gray-900">
                <td className="border border-gray-600 px-4 py-2 text-white">_ga</td>
                <td className="border border-gray-600 px-4 py-2 text-white">Google Analytics - distinguishes unique users</td>
                <td className="border border-gray-600 px-4 py-2 text-white">2 years</td>
              </tr>
              <tr>
                <td className="border border-gray-600 px-4 py-2 text-white">_gid</td>
                <td className="border border-gray-600 px-4 py-2 text-white">Google Analytics - distinguishes unique users</td>
                <td className="border border-gray-600 px-4 py-2 text-white">24 hours</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold mb-3 text-white">Managing Cookies</h2>
        <p className="text-white leading-relaxed mb-3">
          Most web browsers allow you to manage your cookie preferences. You can:
        </p>
        <ul className="list-disc list-inside text-white leading-relaxed space-y-2 ml-4">
          <li>Delete cookies from your device</li>
          <li>Block cookies by activating the setting on your browser that allows you to refuse the setting of all or some cookies</li>
          <li>Set your browser to notify you when you receive a cookie</li>
        </ul>
        <p className="text-white leading-relaxed mt-3">
          Please note that if you choose to block or delete cookies, you may not be able to access certain areas or features of our website, and some services may not function properly.
        </p>
        <div className="mt-6">
          <button 
            onClick={revokeCookieConsent}
            className="px-6 py-3 bg-pink-500 hover:bg-pink-600 text-white rounded-md transition-colors"
          >
            Manage Cookie Settings
          </button>
        </div>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold mb-3 text-white">Cookie Technology</h2>
        <p className="text-white leading-relaxed">
          Our site uses <a href="https://github.com/osano/cookieconsent" target="_blank" rel="noopener noreferrer" className="text-pink-500 hover:underline">Osano Cookie Consent</a>, 
          a lightweight JavaScript plugin for alerting users about the use of cookies on our website. This tool is designed to help us comply with privacy regulations 
          such as the EU Cookie Law, CCPA, and GDPR.
        </p>
      </section>

      <section className="mb-6">
        <h2 className="text-2xl font-semibold mb-3 text-white">Changes to Our Cookie Policy</h2>
        <p className="text-white leading-relaxed">
          We may update our Cookie Policy from time to time. We will notify you of any changes by posting the new Cookie Policy on this page and updating the "Last Updated" date.
          You are advised to review this Cookie Policy periodically for any changes.
        </p>
      </section>

      <section>
        <h2 className="text-2xl font-semibold mb-3 text-white">Contact Us</h2>
        <p className="text-white leading-relaxed mb-3">
          If you have any questions about our Cookie Policy, please contact us:
        </p>
        <address className="not-italic text-white leading-relaxed">
          Email: <a href="mailto:privacy@humanai.io" className="text-pink-500 hover:underline">privacy@humanai.io</a><br />
          Phone: +1‑505‑944‑5572<br />
          Mail: HumanAI Ventures LLC – Privacy Team, 520 Douglas St. Unit 203, West Sacramento, CA 95605, USA
        </address>
      </section>
    </div>
  );
};

export default CookiePolicy; 